import { Select } from '@lunary/ui-web/select'
import { useState } from 'react'
import type { AdminUserResponseDto } from '@lunary/api-client'
import { ApiError } from '../../shared/api/client'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { MergeDialog } from './merge-dialog'
import { ROLES, ROLE_LABELS } from './labels'
import { useSetRole, useUserActions } from './queries'
import styles from './user-card.module.css'

type Dialog = 'block' | 'unblock' | 'revoke' | 'merge' | null

function errorText(error: Error | null): string | null {
  if (!error) return null
  return error instanceof ApiError ? error.message : 'Не получилось, попробуйте ещё раз'
}

export function UserActions({
  user,
  isSelf,
  canSetRole,
  lockedByAdmin,
}: {
  user: AdminUserResponseDto
  isSelf: boolean
  canSetRole: boolean
  lockedByAdmin: boolean
}) {
  const [dialog, setDialog] = useState<Dialog>(null)
  const setRole = useSetRole(user.id)
  const actions = useUserActions(user.id)
  const close = () => {
    setDialog(null)
    actions.block.reset()
    actions.unblock.reset()
    actions.revokeSessions.reset()
  }

  return (
    <section className="card-panel" aria-labelledby="user-actions-title">
      <h2 id="user-actions-title">Управление</h2>
      <label className={styles.field}>
        <span className="muted">Роль</span>
        <Select
          aria-label="Роль"
          value={user.role}
          disabled={setRole.isPending || isSelf || !canSetRole}
          options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
          onChange={(v) => {
            const next = ROLES.find((r) => r === v)
            if (next && next !== user.role) setRole.mutate(next)
          }}
        />
      </label>
      {setRole.error && (
        <p className="error-box" role="alert">
          {errorText(setRole.error)}
        </p>
      )}
      {isSelf && <p className="muted">Свою роль и доступ менять нельзя.</p>}
      {!canSetRole && !isSelf && <p className="muted">Роли назначает только admin.</p>}
      <div className={styles.actions}>
        <button className="secondary" disabled={isSelf || lockedByAdmin} onClick={() => setDialog('revoke')}>
          Завершить сессии
        </button>
        {user.blocked_at ? (
          <button className="ok" disabled={lockedByAdmin} onClick={() => setDialog('unblock')}>
            Разблокировать
          </button>
        ) : (
          <button className="danger" disabled={isSelf || lockedByAdmin} onClick={() => setDialog('block')}>
            Заблокировать
          </button>
        )}
        <button className="secondary" disabled={lockedByAdmin} onClick={() => setDialog('merge')}>
          Объединить с…
        </button>
      </div>

      <ConfirmDialog
        open={dialog === 'block'}
        title="Заблокировать пользователя?"
        text="Все его сессии завершатся сразу, войти снова он не сможет, пока вы не разблокируете."
        confirmLabel="Заблокировать"
        danger
        reasonLabel="Причина (видна только в админке)"
        pending={actions.block.isPending}
        error={errorText(actions.block.error)}
        onConfirm={(reason) => actions.block.mutate(reason, { onSuccess: close })}
        onClose={close}
      />
      <ConfirmDialog
        open={dialog === 'unblock'}
        title="Разблокировать?"
        text="Пользователь снова сможет войти любым способом."
        confirmLabel="Разблокировать"
        pending={actions.unblock.isPending}
        error={errorText(actions.unblock.error)}
        onConfirm={() => actions.unblock.mutate(undefined, { onSuccess: close })}
        onClose={close}
      />
      <ConfirmDialog
        open={dialog === 'revoke'}
        title="Завершить все сессии?"
        text="Пользователя выкинет из всех приложений в течение 15 минут, войти заново он сможет."
        confirmLabel="Завершить"
        pending={actions.revokeSessions.isPending}
        error={errorText(actions.revokeSessions.error)}
        onConfirm={() => actions.revokeSessions.mutate(undefined, { onSuccess: close })}
        onClose={close}
      />
      <MergeDialog open={dialog === 'merge'} user={user} onClose={close} />
    </section>
  )
}
